import { useEffect, useMemo, useState } from 'react'
import { apiService } from '@/services/api'
import type { PaginatedResponse } from '@/features/configuracion/catalogos/types/catalogs.types'

interface ListParams {
  q?: string
  page?: number
  limit?: number
  showDeleted?: boolean
  specialized?: { type: 'nivel' | 'tipo' | 'codigo'; value: string | number }
}

export function useCatalogList<T>(apiBase: string, params: ListParams = {}) {
  const [data, setData] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { q = '', page = 1, limit = 10, showDeleted = false, specialized } = params

  const endpoint = useMemo(() => {
    if (showDeleted) return `${apiBase}/deleted`
    if (specialized) return `${apiBase}/${specialized.type}/${encodeURIComponent(String(specialized.value))}`
    return apiBase
  }, [apiBase, showDeleted, specialized])

  const query = useMemo(() => {
    const p = new URLSearchParams()
    if (q) p.append('q', q)
    if (!specialized) {
      p.append('page', String(page))
      p.append('limit', String(limit))
    }
    return p.toString()
  }, [q, page, limit, specialized])

  const url = query ? `${endpoint}?${query}` : endpoint

  const fetchList = async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await apiService['request']<PaginatedResponse<T> | T[]>(url)
      // normalize
      const arr = Array.isArray(resp) ? resp : resp.data
      const tot = Array.isArray(resp) ? arr.length : (resp.total ?? arr.length)
      setData(arr)
      setTotal(tot)
    } catch (eCrudo: unknown) {
      const e = eCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(e?.message || 'Error al cargar el catálogo')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  return { data, total, loading, error, refetch: fetchList }
}
